import React, { useEffect, useState } from 'react';
import { PhoneInput } from 'react-international-phone';
import { PageHeader } from '../../components/PageHeader';
import { FormGroup } from '../../components/FormGroup';
import { SubmitButton } from '../../components/SubmitButton';
import 'react-international-phone/style.css';
import { getAccountService, getRootScope } from '../../services/angular-bridge';
import { notifySuccess } from '../../services/notify';
import { showApiErrorToast } from '../../services/errorToast';
import { api } from '../../services/api';
import { useFormState } from '../../hooks/useFormState';

interface FormState extends Record<string, unknown> {
  firstName: string;
  lastName: string;
  phone: string;
}

interface FieldErrors {
  firstName?: string;
  lastName?: string;
}

function validate(form: FormState): FieldErrors {
  const errors: FieldErrors = {};
  if (!form.firstName.trim()) errors.firstName = 'First Name is required';
  else if (form.firstName.length > 255) errors.firstName = 'First Name must be less than 255 characters';
  if (!form.lastName.trim()) errors.lastName = 'Last Name is required';
  else if (form.lastName.length > 255) errors.lastName = 'Last Name must be less than 255 characters';
  return errors;
}

export function SettingsPage() {
  const accountService = getAccountService();
  const account = accountService.Get() ?? {};

  const form = useFormState<FormState>({
    firstName: account.firstName ?? '',
    lastName: account.lastName ?? '',
    phone: account.phone ?? '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const deregister = getRootScope().$on('AccountService.accountUpdated', (_event: unknown, updated: { firstName?: string; lastName?: string; phone?: string }) => {
      form.reset({
        firstName: updated.firstName ?? '',
        lastName: updated.lastName ?? '',
        phone: updated.phone ?? '',
      });
    });
    return deregister;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errors = validate(form.values);
  const isInvalid = Object.keys(errors).length > 0;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    form.touchAll();
    if (isInvalid || saving || !form.dirty) return;

    setSaving(true);
    try {
      const updated = await api.put<any>('/account/current', {
        firstName: form.values.firstName,
        lastName: form.values.lastName,
        phone: form.values.phone,
      });
      getRootScope().$apply(() => accountService.SetAccount(updated));
      notifySuccess({ title: 'Account Updated', timeout: 2500 });
      form.reset(form.values);
    } catch (error) {
      showApiErrorToast(error);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <PageHeader icon="fas fa-user" title="Settings" />

      <div className="row">
        <div className="col-md-8">
          <form className="form-horizontal" onSubmit={handleSubmit} noValidate>
            <legend>Account Details</legend>

            <FormGroup label="First Name" htmlFor="firstName" error={form.touched.firstName ? errors.firstName : undefined}>
              <input
                type="text"
                id="firstName"
                name="firstName"
                className="form-control"
                value={form.values.firstName}
                onChange={e => form.set('firstName', e.target.value)}
                onBlur={() => form.blur('firstName')}
                disabled={saving}
                maxLength={255}
              />
            </FormGroup>

            <FormGroup label="Last Name" htmlFor="lastName" error={form.touched.lastName ? errors.lastName : undefined}>
              <input
                type="text"
                id="lastName"
                name="lastName"
                className="form-control"
                value={form.values.lastName}
                onChange={e => form.set('lastName', e.target.value)}
                onBlur={() => form.blur('lastName')}
                disabled={saving}
                maxLength={255}
              />
            </FormGroup>

            <FormGroup label="Phone" htmlFor="phone">
              <PhoneInput
                inputProps={{ id: 'phone', name: 'phone', autoComplete: 'off' }}
                value={form.values.phone}
                onChange={phone => form.set('phone', phone)}
                disabled={saving}
                inputClassName="form-control"
              />
            </FormGroup>

            <div className="form-group">
              <div className="col-sm-6 col-sm-offset-2">
                <SubmitButton
                  pending={saving}
                  pendingLabel="Saving..."
                  disabled={!form.dirty || isInvalid}
                >Save</SubmitButton>
              </div>
            </div>
          </form>

        </div>
      </div>
    </div>
  );
}
